import { PlusIcon, SealCheckIcon, TrashIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { type ReactNode, type SubmitEvent, useState } from 'react';

import { EmptyState } from '@/components/empty-state';
import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { useSetting } from '@/features/settings';
import type { LogbookCheck, LogbookCheckKind } from '@/lib/db';
import { logbookChecks } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';

import { isCalendarDate } from './columns';
import { evaluateExpiryReminder, type CurrencyLevel } from './currency';

/**
 * Flight tests, reviews and competency checks — the records a licence
 * actually hangs on.
 *
 * Kept off the logbook page on purpose. A flight review *is* a flight, and
 * it has a row on the flight pages like any other; what makes it matter is
 * the examiner, the certification and the date the next one falls due, and
 * putting those on a logbook row would mean an examiner column sitting
 * empty on every circuit a pilot ever flies. CAA 1373's flight pages have
 * no such column, and neither does this app's.
 *
 * Every date here is one the pilot entered. Nothing on this page computes
 * an expiry: the interval depends on the rating, the licence and the rule
 * the check was flown under, and a date this app worked out for itself
 * would be a date somebody might rely on — the same line `currency.ts`
 * draws between reminding and ruling.
 */

const KINDS: readonly { id: LogbookCheckKind; label: string }[] = [
  { id: 'flight-test', label: 'Flight Test' },
  { id: 'flight-review', label: 'Flight Review' },
  { id: 'competency-demonstration', label: 'Competency Demonstration' },
  { id: 'flight-crew-competency-check', label: 'Flight Crew Competency Check' },
];

function kindLabel(kind: LogbookCheckKind): string {
  return KINDS.find((option) => option.id === kind)?.label ?? kind;
}

/**
 * How far out an expiry starts showing as "expiring soon".
 *
 * A month, matching the currency page's own reminders: long enough to book
 * an examiner, short enough that the warning still means something when it
 * appears.
 */
const WARN_WITHIN_DAYS = 30;

const INPUT_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-base outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:text-sm';

const LEVEL_STYLE: Record<CurrencyLevel, string> = {
  ok: 'border-border text-muted-foreground',
  expiring: 'border-accent text-foreground bg-accent-soft',
  expired: 'border-danger text-danger-foreground bg-danger',
  unknown: 'border-border text-muted-foreground',
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

interface FormState {
  kind: LogbookCheckKind;
  date: string;
  purpose: string;
  expiryDate: string;
  examiner: string;
  caaClientNumber: string;
  certification: string;
  notes: string;
}

function blankForm(today: string): FormState {
  return {
    kind: 'flight-review',
    date: today,
    purpose: '',
    expiryDate: '',
    examiner: '',
    caaClientNumber: '',
    certification: '',
    notes: '',
  };
}

export function ChecksPage() {
  const [timeZone] = useSetting('timeZone');
  const asOf = formatLocalDate(new Date().toISOString(), { format: 'iso', timeZone });
  const checks = useLiveQuery(() => logbookChecks.listRecent(), [], null);

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  };

  const save = async (event: SubmitEvent) => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    if (!isCalendarDate(form.date)) {
      setError('Enter the date as YYYY-MM-DD.');
      return;
    }
    if (form.expiryDate !== '' && !isCalendarDate(form.expiryDate)) {
      setError('Enter the expiry date as YYYY-MM-DD, or leave it blank.');
      return;
    }
    const now = new Date().toISOString();
    const text = (value: string) => value.trim();
    const optional = (key: keyof LogbookCheck, value: string) =>
      text(value) === '' ? {} : { [key]: text(value) };

    await logbookChecks.put({
      id: crypto.randomUUID(),
      kind: form.kind,
      date: form.date,
      ...optional('purpose', form.purpose),
      ...optional('expiryDate', form.expiryDate),
      ...optional('examiner', form.examiner),
      ...optional('caaClientNumber', form.caaClientNumber),
      ...optional('certification', form.certification),
      ...optional('notes', form.notes),
      createdAt: now,
      updatedAt: now,
    });
    setForm(null);
    setError(null);
  };

  if (checks === null) {
    return <p className="text-muted-foreground text-sm">Opening the local database…</p>;
  }

  /*
   * One button, placed where the eye already is: inside the empty state when
   * there is nothing yet (every other empty state holds its own action), above
   * the table once there is.
   */
  const recordButton = (
    <Button
      size="sm"
      variant={checks.length === 0 ? 'primary' : 'outline'}
      onClick={() => {
        setForm(blankForm(asOf));
      }}
    >
      <PlusIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
      Record a check
    </Button>
  );

  return (
    <div className="space-y-4">
      {form === null ? (
        checks.length === 0 ? null : (
          recordButton
        )
      ) : (
        <form
          className="border-border bg-surface rounded-container flex flex-col gap-4 border p-4"
          onSubmit={(event) => {
            void save(event);
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Type">
              <select
                className={INPUT_CLASS}
                value={form.kind}
                onChange={(e) => {
                  set('kind', e.target.value as LogbookCheckKind);
                }}
              >
                {KINDS.map((kind) => (
                  <option key={kind.id} value={kind.id}>
                    {kind.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date">
              <input
                className={INPUT_CLASS}
                type="date"
                value={form.date}
                onChange={(e) => {
                  set('date', e.target.value);
                }}
              />
            </Field>
            <Field label="Purpose">
              <input
                className={INPUT_CLASS}
                value={form.purpose}
                placeholder="PPL, type rating, IR renewal…"
                onChange={(e) => {
                  set('purpose', e.target.value);
                }}
              />
            </Field>
            <Field label="Expiry date">
              <input
                className={INPUT_CLASS}
                type="date"
                value={form.expiryDate}
                onChange={(e) => {
                  set('expiryDate', e.target.value);
                }}
              />
            </Field>
            <Field label="Examiner / instructor">
              <input
                className={INPUT_CLASS}
                value={form.examiner}
                onChange={(e) => {
                  set('examiner', e.target.value);
                }}
              />
            </Field>
            <Field label="CAA client number">
              <input
                className={INPUT_CLASS}
                value={form.caaClientNumber}
                onChange={(e) => {
                  set('caaClientNumber', e.target.value);
                }}
              />
            </Field>
            <Field label="Certification">
              <input
                className={INPUT_CLASS}
                value={form.certification}
                placeholder="Signed in logbook p.42"
                onChange={(e) => {
                  set('certification', e.target.value);
                }}
              />
            </Field>
            <Field label="Notes">
              <input
                className={INPUT_CLASS}
                value={form.notes}
                onChange={(e) => {
                  set('notes', e.target.value);
                }}
              />
            </Field>
          </div>

          {error === null ? null : (
            <p role="alert" className="text-danger text-xs">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm">
              Save check
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setForm(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {checks.length === 0 ? (
        <EmptyState
          icon={SealCheckIcon}
          title="No checks recorded"
          description="Flight tests, flight reviews and competency checks."
        >
          {form === null ? recordButton : null}
        </EmptyState>
      ) : (
        <section className="border-border bg-surface rounded-container border p-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-border text-muted-foreground border-b text-xs">
                <th className="py-1 text-left font-normal">Type</th>
                <th className="py-1 text-left font-normal">Date</th>
                <th className="py-1 text-left font-normal">Purpose</th>
                <th className="py-1 text-left font-normal">Examiner</th>
                <th className="py-1 text-left font-normal">Expires</th>
                <th className="py-1 text-left font-normal">Certification</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {checks.map((check) => {
                /*
                 * The same expiry evaluation the currency page runs, so a
                 * check and the reminder it feeds cannot disagree about
                 * whether something has run out.
                 */
                const status =
                  check.expiryDate === undefined
                    ? { level: 'unknown' as CurrencyLevel }
                    : evaluateExpiryReminder(
                        {
                          id: check.id,
                          label: kindLabel(check.kind),
                          // The rule behind the date is on the certificate
                          // the examiner signed, not something this app
                          // supplies — so the purpose stands in for it.
                          ruleReference: check.purpose ?? '',
                          expiryDate: check.expiryDate,
                          warnWithinDays: WARN_WITHIN_DAYS,
                        },
                        asOf,
                      );
                return (
                  <tr key={check.id} className="border-border border-b last:border-0">
                    <td className="py-1.5">{kindLabel(check.kind)}</td>
                    <td className="py-1.5">{check.date}</td>
                    <td className="text-muted-foreground py-1.5">{check.purpose ?? '—'}</td>
                    <td className="text-muted-foreground py-1.5">
                      {check.examiner ?? '—'}
                      {check.caaClientNumber === undefined ? null : (
                        <span className="text-muted-foreground"> ({check.caaClientNumber})</span>
                      )}
                    </td>
                    <td className="py-1.5">
                      {check.expiryDate === undefined ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={`rounded-control inline-flex items-center border px-2 py-0.5 text-xs ${LEVEL_STYLE[status.level]}`}
                        >
                          {check.expiryDate}
                        </span>
                      )}
                    </td>
                    <td className="text-muted-foreground py-1.5">{check.certification ?? '—'}</td>
                    <td className="py-1.5 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`Delete the ${kindLabel(check.kind)} of ${check.date}`}
                        onClick={() => {
                          void logbookChecks.delete(check.id);
                        }}
                      >
                        <TrashIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
